import { Filters, ORDER_VALUE, SMsWithModels } from '@spo/logic';
import {
  PatchApiSpoModelsBodyUpdateModelMassEditItem,
  SpoModelsUpdateResponse,
} from '@spo/types';
import { VpLoader } from '@vtmn-play/react';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useModels } from '../../hooks/useModel';
import { useModelsMassEdit } from '../../hooks/useModelsMassEdit';
import { usePersonalOfferTable } from '../../hooks/usePersonalOfferTable';
import { useUserConfig } from '../../hooks/useUserConfig';
import { Footer } from '../footer';
import { MassEditInputs } from '../massEditInputs';
import { StatusState } from '../navigation';
import { ModelAccordion } from '../table/modelsTable/modelAccordion';
import { ModalErrorType } from './MassEdit';
import { MassEditErrorModal } from './MassEditErrorModal';
import { MassEditStatusWarningModal } from './MassEditStatusWarningModal';

type ModelMassEditProps = {
  selectedFilters: Filters;
  modelIds: string[];
};

export const ModelMassEdit = ({
  selectedFilters,
  modelIds,
}: ModelMassEditProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusSelection, setStatusSelection] = useState<StatusState[]>([]);
  const [startDateSelected, setStartDateSelected] = useState<Date[]>([]);
  const [endDateSelected, setEndDateSelected] = useState<Date[]>([]);
  const [modelUpdateMassEdit, setModelUpdateMassEdit] = useState<
    PatchApiSpoModelsBodyUpdateModelMassEditItem[]
  >([]);
  const [modelMassEditErrorResponse, setModelMassEditErrorResponse] = useState<
    SpoModelsUpdateResponse | undefined
  >([]);
  const [massEditStatusWarningModal, setMassEditStatusWarningModal] =
    useState(false);
  const [sMsWithModels, setSMsWithModels] = useState<SMsWithModels[]>();
  const [disabledDates, setDisabledDates] = useState(false);
  const [disabledStartDateInput, setDisabledStartDateInput] = useState(false);

  const { data: userConfigData } = useUserConfig();
  const dateFormatted =
    userConfigData &&
    userConfigData.offerDate &&
    format(Date.parse(userConfigData.offerDate), 'dd MMMM yyyy');

  const { data: modelData } = useModels(
    { smIds: [], modelIds },
    userConfigData?.offerDate,
  );

  const smIds = modelData?.map((model) => model.superModelId);

  //TODO instead of using this huuuuuuge service just to get the sm label we should create another query to get SMs by their ids
  // 2 benefits to do that :
  // 1) we won't trigger an empty response when the query and call the backend when SMIds is empty and it should not (here in this cas it should always be populated)
  // 2) we won't fetch many labels we don't event need.
  const { data: superModelsTableData } = usePersonalOfferTable(
    userConfigData,
    { start: 0, end: 99 },
    {
      id: 'startDate',
      order: ORDER_VALUE.DESC,
    },
    selectedFilters,
    smIds,
  );

  const {
    data: modelMassEditResponse,
    isError: isErrorModelMassEdit,
    isLoading: isLoadingModelsMassEdit,
    refetch,
  } = useModelsMassEdit(modelUpdateMassEdit);

  useEffect(() => {
    const modelMassEditError = modelMassEditResponse?.filter(
      (model) => model.status !== 200,
    );

    if (!modelMassEditError?.length && modelMassEditResponse) {
      navigate('/personal-offer/my-view', { state: { displaySnackBar: true } });
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setModelMassEditErrorResponse(modelMassEditError);
    }
  }, [modelMassEditResponse]);

  useEffect(() => {
    if (selectedStatus === 'CANCELLED') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStartDateSelected([]);
      setEndDateSelected([]);
      setDisabledDates(true);
    } else {
      setDisabledDates(false);
    }
  }, [selectedStatus]);

  const isValidateActionDisabled =
    !startDateSelected.length && !endDateSelected.length && !selectedStatus;

  useEffect(() => {
    if (modelData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisabledStartDateInput(
        modelData.filter(
          (model) => model.beginDate > format(new Date(), 'yyyy-MM-dd'),
        ).length === 0,
      );
    }
  }, [modelData]);

  useEffect(() => {
    if (modelData && superModelsTableData?.data) {
      const sMWithModel = superModelsTableData?.data.map((sm) => ({
        smId: sm.id,
        description: sm.descriptionEn,
        models: modelData
          .filter((model) => model.superModelId === sm.id)
          .filter((model) => model.status !== 'CANCELLED'),
      }));
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSMsWithModels(sMWithModel);
    }
  }, [superModelsTableData, modelData]);

  const cancelModelMassEdit = () => {
    navigate(`/personal-offer/my-view?modelIds=${modelIds}`);
  };

  const preValidateModelMassEdit = () => {
    if (selectedStatus === 'VALIDATED') {
      setMassEditStatusWarningModal(true);
    } else {
      validateModelMassEdit();
    }
  };

  const validateModelMassEdit = () => {
    if (sMsWithModels) {
      setMassEditStatusWarningModal(false);
      const modelEdited: PatchApiSpoModelsBodyUpdateModelMassEditItem[] = [];
      sMsWithModels.forEach((sm) => {
        sm.models.forEach((model) => {
          modelEdited.push({
            id: model.id,
            ...(startDateSelected.length > 0 && {
              startDate: format(startDateSelected[0], 'yyyy-MM-dd'),
            }),
            ...(endDateSelected.length > 0 && {
              endDate: format(endDateSelected[0], 'yyyy-MM-dd'),
            }),
            ...(selectedStatus && { status: selectedStatus }),
          });
        });
      });
      setModelUpdateMassEdit([...new Set(modelEdited)]);
    }
  };

  useEffect(() => {
    if (sMsWithModels) {
      const modelsStatus = [
        ...new Set(
          sMsWithModels.flatMap((sm) =>
            sm.models.flatMap((model) => model.status),
          ),
        ),
      ];

      const getAvailableStatus = () => {
        const hasSeveralStatus = [...new Set(modelsStatus)].length > 1;
        if (hasSeveralStatus) {
          return [
            {
              status: 'PROPOSAL_VALIDATED',
              disabled: true,
            },
            { status: 'CANCELLED', disabled: false },
            {
              status: 'VALIDATED',
              disabled: true,
            },
          ];
        } else {
          return [
            {
              status: 'PROPOSAL_VALIDATED',
              disabled: modelsStatus[0] !== 'CREATED',
            },
            { status: 'CANCELLED', disabled: false },
            {
              status: 'VALIDATED',
              disabled: modelsStatus[0] !== 'PROPOSAL_VALIDATED',
            },
          ];
        }
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatusSelection(getAvailableStatus());
    }
  }, [sMsWithModels]);

  const isMassEditRequestInError = useMemo(
    () => modelMassEditErrorResponse || isErrorModelMassEdit,
    [modelMassEditErrorResponse, isErrorModelMassEdit],
  );

  return (
    <>
      <MassEditStatusWarningModal
        validate={validateModelMassEdit}
        cancel={() => setMassEditStatusWarningModal(false)}
        isOpen={massEditStatusWarningModal}
      />
      <div data-testid="model-mass-edit" className="model-mass-edit">
        <MassEditInputs
          startDateSelected={startDateSelected}
          endDateSelected={endDateSelected}
          setStartDateSelected={setStartDateSelected}
          setEndDateSelected={setEndDateSelected}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          disabledStartDateInput={disabledStartDateInput}
          disabledDates={disabledDates}
          massEditTitleKey="header.mass-edit_models_title"
          statusSelection={statusSelection}
        />
        {sMsWithModels ? (
          <div className="container_mass-edit_table">
            {sMsWithModels?.map((sm) => {
              const { smId, description, models } = sm;
              return (
                <ModelAccordion
                  key={`model-accordion-${smId}`}
                  smId={smId}
                  description={description}
                  offerDate={dateFormatted}
                  models={models}
                  withCheckboxes={false}
                />
              );
            })}
          </div>
        ) : (
          <div
            data-testid="model-mass-edit-table-loading"
            className="loading-container-model-mass-edit"
          >
            <VpLoader size="medium" className="loading-model-mass-edit" />
          </div>
        )}
        {isMassEditRequestInError && (
          <MassEditErrorModal
            errorResponse={modelMassEditErrorResponse || []}
            type={ModalErrorType.MODEL}
            refetch={refetch}
          />
        )}
      </div>
      <Footer
        ctaLabel={t('footer.save-and-quit-button')}
        validateAction={preValidateModelMassEdit}
        cancelAction={cancelModelMassEdit}
        isValidateActionDisabled={isValidateActionDisabled}
        isLoading={isLoadingModelsMassEdit}
      />
    </>
  );
};
